/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useId } from 'react';
import { useForm } from 'react-final-form';
import FirstNameField from './FirstNameField';
import MiddleNameField from './MiddleNameField';
import LastNameField from './LastNameField';
import AddressField from './AddressField';
import CityField from './CityField';
import StateField from './StateField';
import ZipCodeField from './ZipCodeField';
import DateOfBirthField from './DateOfBirthField';
import PhoneNumberField from './PhoneNumberField';
import SocialSecurityNumberField from './SocialSecurityNumberField';
import {
  Button,
  Form,
  Grid,
  Heading,
  Paragraph,
  Popover,
  Popper,
  TextButton,
  VisuallyHidden,
} from '@zillow/constellation';
import { usePidAnswers, useTermSet } from '../../contexts/IdVerificationContext';
import { TermSet } from '../../../..';
import PrivacyCard from '../../PrivacyCard';
import { useEffectOnce } from 'react-use';

const renterTemplateAreaXl = [
  'head head head head head head privacy privacy privacy privacy privacy',
  'subHead subHead subHead subHead subHead subHead privacy privacy privacy privacy privacy',
  'first first first first first first privacy privacy privacy privacy privacy',
  'middle middle middle middle middle middle privacy privacy privacy privacy privacy',
  'last last last last last last privacy privacy privacy privacy privacy',
  'address address address address address address privacy privacy privacy privacy privacy',
  'city city state state zip zip privacy privacy privacy privacy privacy',
  'dob dob dob phone phone phone privacy privacy privacy privacy privacy',
  'ssn ssn ssn . . . privacy privacy privacy privacy privacy',
  'popper popper popper popper popper popper privacy privacy privacy privacy privacy',
];

const renterTemplateAreaLg = [
  'head head head head head head privacy privacy privacy privacy privacy',
  'subHead subHead subHead subHead subHead subHead privacy privacy privacy privacy privacy',
  'first first first first first first privacy privacy privacy privacy privacy',
  'middle middle middle middle middle middle privacy privacy privacy privacy privacy',
  'last last last last last last privacy privacy privacy privacy privacy',
  'address address address address address address privacy privacy privacy privacy privacy',
  'city   city  city  city  city  city  privacy privacy privacy privacy privacy',
  'state  state state state state state privacy privacy privacy privacy privacy',
  'zip    zip   zip   zip   zip   zip   privacy privacy privacy privacy privacy',
  'dob dob dob phone phone phone privacy privacy privacy privacy privacy',
  'ssn ssn ssn . . . privacy privacy privacy privacy privacy',
  'popper popper popper popper popper popper privacy privacy privacy privacy privacy',
];

const landlordTemplateArea = [
  'head head head head head head privacy privacy privacy privacy privacy',
  'subHead subHead subHead subHead subHead subHead privacy privacy privacy privacy privacy',
  'first first first first first first privacy privacy privacy privacy privacy',
  'middle middle middle middle middle middle privacy privacy privacy privacy privacy',
  'last last last last last last privacy privacy privacy privacy privacy',
  'address address address address address address privacy privacy privacy privacy privacy',
  'city city state state zip zip privacy privacy privacy privacy privacy',
  'ssn ssn ssn . . . privacy privacy privacy privacy privacy',
  'popper popper popper popper popper popper privacy privacy privacy privacy privacy',
];

const defaultRenterTemplateArea = [
  'head',
  'subHead',
  'first',
  'middle',
  'last',
  'address',
  'city',
  'state',
  'zip',
  'dob',
  'phone',
  'ssn',
  'popper',
  'privacy',
];

const defaultLandlordTemplateArea = [
  'head',
  'subHead',
  'first',
  'middle',
  'last',
  'address',
  'city',
  'state',
  'zip',
  'ssn',
  'popper',
  'privacy',
];

interface PersonalInfoStepContentProps {
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

export default function PersonalInfoStepContent({ handleSubmit }: PersonalInfoStepContentProps) {
  const idVerificationId = useId();
  const termSet = useTermSet();
  const pidAnswers = usePidAnswers();
  const form = useForm();

  useEffectOnce(() => {
    form.batch(() => {
      Object.entries(pidAnswers).forEach(([fieldName, fieldValue]) => {
        if (fieldValue && String(fieldValue).trim() != '') {
          form.blur(fieldName);
        }
      });
    });
  });

  return (
    <Form onSubmit={handleSubmit}>
      <Grid
        display="grid"
        gridTemplateColumns={{
          default: 'repeat(1, 1fr)',
          lg: termSet === TermSet.RenterScreening ? 'repeat(11, 1fr)' : 'repeat(1, 1fr)',
          xl: 'repeat(11, 1fr)',
        }}
        gap="md"
        gridTemplateAreas={{
          default: termSet === TermSet.LandlordScreening ? defaultLandlordTemplateArea : defaultRenterTemplateArea,
          lg: termSet === TermSet.LandlordScreening ? defaultLandlordTemplateArea : renterTemplateAreaLg,
          xl: termSet === TermSet.LandlordScreening ? landlordTemplateArea : renterTemplateAreaXl,
        }}
      >
        <Grid gridArea="head">
          <Heading level={4} as="h2">
            Provide your details for verification
          </Heading>
        </Grid>
        <Grid gridArea="subHead">
          <Paragraph>
            {termSet === TermSet.RenterScreening
              ? 'This information will be sent to Experian and CIC to verify your identity and create credit and background reports.'
              : 'This information helps our partners, Experian and CIC, verify your identity so you can review applicants’ information and find a suitable renter. '}
          </Paragraph>
        </Grid>
        <Grid gridArea="first">
          <FirstNameField />
        </Grid>
        <Grid gridArea="middle">
          <MiddleNameField />
        </Grid>
        <Grid gridArea="last">
          <LastNameField />
        </Grid>
        <Grid gridArea="address">
          <AddressField />
        </Grid>
        <Grid gridArea="city">
          <CityField />
        </Grid>
        <Grid gridArea="state">
          <StateField />
        </Grid>
        <Grid gridArea="zip">
          <ZipCodeField />
        </Grid>
        {termSet === TermSet.RenterScreening && (
          <>
            <Grid gridArea="dob">
              <DateOfBirthField />
            </Grid>
            <Grid gridArea="phone">
              <PhoneNumberField />
            </Grid>
          </>
        )}
        <Grid gridArea="ssn">
          <SocialSecurityNumberField />
        </Grid>
        <Grid gridArea="privacy">
          <PrivacyCard />
        </Grid>
        <Grid gridArea="popper">
          <Popper
            triggered={
              <Popover
                renderHeader={<Heading level={2}>Social Security number (SSN)</Heading>}
                renderBody={
                  <Paragraph>
                    Providing an SSN helps verify your identity faster, but is not required. If this field is left
                    blank, Experian may need to ask additional questions to verify your identity.
                  </Paragraph>
                }
              />
            }
          >
            <TextButton onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => e.preventDefault()}>
              Why do we ask for your Social Security number?
            </TextButton>
          </Popper>
        </Grid>
      </Grid>
      <VisuallyHidden>
        <Button type="submit" id={`${idVerificationId}-submit-pid`}>
          Submit
        </Button>
      </VisuallyHidden>
    </Form>
  );
}
